import React, { type ReactNode, useEffect, cloneElement, ReactElement } from "react";
import { type Control, type PathValue, type Path, useController, ControllerRenderProps, useFormContext, useFormState } from "react-hook-form";
import { SupInput, SupSummary, SupTooltip } from "@repo/core-ui";
import { twJoin } from "tailwind-merge";

import { DragableCell } from "./DragableCell";
import type { FieldValues, ArrayFieldColumn, RowConfig, CellConfig } from "../types";

type ValueRenderers<R extends FieldValues> = Pick<ArrayFieldColumn<R>, "renderValue" | "renderField">;

interface EditableCellProps<R extends FieldValues, N extends string> extends ValueRenderers<R> {
  path: N;
  name: string;
  editable: boolean;
  rowId: string;
  rowIndex: number;
  colIndex: number;
  defaultValue?: unknown;
  control?: Control<R>;
  rowConfig: RowConfig;
  cellConfig: CellConfig;
  shouldUnregister?: boolean;
  textAlign?: "left" | "right" | "center";
  rowControls: ReactElement;
  row: R;
  move: (id: string, atIndex: number) => void;
  onEdited?: (name: string, index: number) => void;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const defaultRenderValue = (value: unknown): ReactNode => {
  return <span className="overflow-hidden text-ellipsis whitespace-nowrap">{value as ReactNode}</span>;
};

function defaultRenderField<R extends FieldValues>(field: ControllerRenderProps<R>) {
  return <SupInput {...field} fluid />;
}

const TEXT_ALIGNMENT = {
  left: "justify-start",
  center: "justify-center",
  right: "justify-end",
} as const;

export function EditableCell<R extends FieldValues, N extends string>({
  path,
  editable = false,
  rowId,
  rowIndex,
  colIndex,
  name,
  defaultValue,
  control,
  rowConfig,
  cellConfig,
  textAlign,
  shouldUnregister = false,
  rowControls,
  row,
  move,
  renderField = defaultRenderField,
  renderValue = defaultRenderValue,
  onEdited,
  onClick,
}: EditableCellProps<R, N>) {
  const { isValid } = useFormState({ name: path });
  const { setFocus } = useFormContext();
  const fieldPath = `${path}.${rowIndex}.${name}` as Path<R>;

  const { field, fieldState } = useController<R>({
    name: fieldPath,
    defaultValue: defaultValue as PathValue<R, Path<R>>,
    shouldUnregister,
    control,
  });

  useEffect(() => {
    if (editable) setFocus(fieldPath);
  }, [editable, fieldPath]);

  function handleClick() {
    if (!editable && cellConfig.editable) {
      onClick?.();
    }
  }

  const renderContent = (dragRef?: (el: HTMLButtonElement) => void) => (
    <>
      {/* @ts-ignore */}
      {colIndex === 0 && cloneElement(rowControls, { valid: isValid, rowConfig, dragRef })}

      {fieldState.invalid && fieldState.isTouched && fieldState.error?.message && (
        <SupTooltip portal wrapper unmount color="error" placement="top-start" content={fieldState.error.message} className="inline-flex mr-2 cursor-pointer">
          <SupSummary variant="error" />
        </SupTooltip>
      )}

      <span
        tabIndex={editable || !cellConfig.editable ? -1 : 0}
        className={twJoin("inline-flex flex-1 h-full items-center min-w-0", textAlign && TEXT_ALIGNMENT[textAlign], cellConfig.editable ? "cursor-pointer" : "cursor-default")}
        onClick={handleClick}
        onFocus={handleClick}
      >
        {!editable || !cellConfig.editable
          ? renderValue(field.value, row)
          : renderField(
              {
                ...field,
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                value: field.value || defaultValue,
                onBlur: () => {
                  field.onBlur();
                  onEdited?.(name, rowIndex);
                },
              } as ControllerRenderProps<R>,
              fieldState,
              row
            )}
      </span>
    </>
  );

  return colIndex === 0 ? (
    <DragableCell itemType={name} id={rowId} index={rowIndex} className="sup-array-field-cell flex h-full w-full items-center justify-start" onItemMove={move}>
      {renderContent}
    </DragableCell>
  ) : (
    <div className="sup-array-field-cell flex h-full w-full items-center justify-start">{renderContent()}</div>
  );
}
